import classNames from '@sindresorhus/class-names';
import {HeaderProps} from '@ui-kit/src/Header/Header.types';
import {headerTestId} from '@ui-kit/src/Header/headerTestid';
import {useCallback, useEffect, useRef, useState} from 'react';

import Close from '@ui-kit/src/assets/Icons/Close.svg';
import Menu from '@ui-kit/src/assets/Icons/Menu.svg';

import styles from './Links.module.scss';

type Props = {
  isMobileMenuVisible: boolean;
  setMobileMenuVisible: React.Dispatch<React.SetStateAction<boolean>>;
} & Pick<
  HeaderProps,
  'links' | 'onLinkClick' | 'mobileMenu' | 'activeLinkOrder'
>;
export const Links = ({
  links,
  onLinkClick,
  mobileMenu: {mobileMenuTitle, mobileMenuNode},
  isMobileMenuVisible,
  setMobileMenuVisible,
  activeLinkOrder,
}: Props) => {
  const mobileMenu = useRef<HTMLDivElement>(null);
  const [isInIframe, setIsInIframe] = useState(false);

  const onMobileMenu = useCallback(() => {
    if (!isMobileMenuVisible) {
      return setMobileMenuVisible(true);
    }

    if (mobileMenu.current) {
      mobileMenu.current.classList.add(styles.mobileMenu__closeAnimation);
      mobileMenu.current.addEventListener(
        'animationend',
        () => setMobileMenuVisible(false),
        {once: true},
      );
    } else {
      setMobileMenuVisible(false);
    }
  }, [isMobileMenuVisible, setMobileMenuVisible]);

  useEffect(() => {
    if (window && window !== window.parent) {
      setIsInIframe(true);
    }
  }, []);

  const onWrapperClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && isMobileMenuVisible) {
      onMobileMenu();
    }
  };

  return (
    <>
      <div className={styles.linksWrapper}>
        {links.map(({mobileIconCode, ...link}, index) => (
          <a
            {...link}
            onClick={(event) => onLinkClick?.(link, event)}
            href={link.href}
            className={classNames(styles.link, {
              [styles.activeDesktopLink]: index + 1 === activeLinkOrder,
            })}
            target={isInIframe ? '_blank' : link.target}
            key={link.title}
            data-testid={headerTestId.link}
          >
            {link.title}
          </a>
        ))}
      </div>
      <div
        className={classNames(styles.mobileMenuWrapper, {
          [styles.fullScreenWrapper]: isMobileMenuVisible,
        })}
        onClick={onWrapperClick}
      >
        <button
          onClick={onMobileMenu}
          className={styles.menuIcon}
          data-testid={headerTestId.menuButton}
          aria-label={isMobileMenuVisible ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={isMobileMenuVisible}
        >
          <img
            src={isMobileMenuVisible ? Close.src : Menu.src}
            alt=""
            aria-hidden={true}
            loading="lazy"
          />
        </button>
        {isMobileMenuVisible &&
          (mobileMenuNode?.({
            handleHideMenu: onMobileMenu,
            isMenuOpened: isMobileMenuVisible,
            menuRef: mobileMenu,
          }) ?? (
            <div
              className={styles.mobileMenu}
              ref={mobileMenu}
              data-testid={headerTestId.menu}
            >
              <div className={styles.actions}>
                <span className={styles.actions__title}>{mobileMenuTitle}</span>
                <span
                  className={styles.actions__closeButton}
                  onClick={onMobileMenu}
                  data-testid={headerTestId.menuButton}
                >
                  <img src={Close.src} alt="Закрыть меню" />
                </span>
              </div>
              <div className={styles.items}>
                {links.map(({mobileIconCode, ...link}, index) => (
                  <a
                    {...link}
                    key={link.title}
                    href={link.href}
                    onClick={(event) => onLinkClick?.(link, event)}
                    className={classNames(styles.items__link, {
                      [styles.activeMobileLink]: index + 1 === activeLinkOrder,
                    })}
                    data-testid={headerTestId.link}
                  >
                    <div
                      className={styles.items__link_img}
                      dangerouslySetInnerHTML={{__html: mobileIconCode}}
                    />
                    {link.title}
                  </a>
                ))}
              </div>
            </div>
          ))}
      </div>
    </>
  );
};
